import { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { isMobileDevice } from '../../../utility/utility';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';
import config from '../../../config.json';

/**
 * AidaFAQ Component
 * FAQ section from AidasCopy prototype
 * Displays FAQ heading, description, accordion FAQ items, and Discord CTA
 * Desktop: Horizontal layout with heading left, accordion right
 * Mobile: Vertical centered layout
 */

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: "What is a Playcast Host?",
    answer: "A Host is someone who shares their PC with the Playcast network and earns rewards in return."
  },
  {
    question: "How do hosts benefit Playcast?",
    answer: "Hosts dramatically reduce Playcast's costs, deliver far better performance than low-powered cloud servers, and provide smoother gameplay through lower latency. By contributing their powerful gaming PCs, Hosts aren't just improving the network — they're helping power a revolution in how games are played and streamed."
  },
  {
    question: "Is my data safe?",
    answer: "Yes. When Playcast uses your PC during idle time to run interactive demos, this is called Host Mode, where all sessions run inside a separate, secure Windows account — meaning players can never access your desktop, files, or personal data."
  },
  {
    question: "Do I need to leave Playcast open 24/7?",
    answer: "Nope — Playcast runs as a background service. There's no need to have the app open to host. As of right now, we are in a recruitment period: active hosting is not rolled out yet. When hosting is rolled out, Playcast will host only during your idle PC times."
  },
  {
    question: "Do I get access to free games as a Host?",
    answer: "Yes! Currently, Hosts receive access to rewards in exchange for being part of the Playcast network. These are distributed when hitting milestones as a Playcast Host — all you need to do is have our app installed as we focus on our recruitment period. Eventually, rewards will accrue and expand based off of active host time."
  },
  {
    question: "Where can I contact Playcast Support?",
    answer: "Join our Discord and share your issue as clearly and specifically as possible in the support channel — the Playcast team will help as soon as we're available. If your concern involves private information, such as personal rewards or sensitive bug details, please open a ticket instead. This creates a private channel between you and the Playcast team where we can assist you securely."
  }
];

export const AidaFAQ = () => {
  const ref = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, margin: '-100px' });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const answerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const mobileAnswerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Initialize all answer elements to closed state on mount
  useEffect(() => {
    const allRefs = [...answerRefs.current, ...mobileAnswerRefs.current];
    allRefs.forEach((answer) => {
      if (answer) {
        answer.style.maxHeight = '0px';
      }
    });
  }, []);

  // Calculate and set max-height for smooth transitions
  useEffect(() => {
    const allRefs = [...answerRefs.current, ...mobileAnswerRefs.current];
    allRefs.forEach((answer, index) => {
      if (!answer) return;

      const actualIndex = index < faqData.length ? index : index - faqData.length;

      if (openIndex === actualIndex) {
        // Temporarily set to 'none' to measure actual height
        answer.style.maxHeight = 'none';
        const height = answer.scrollHeight;
        answer.style.maxHeight = '0px';
        // Force reflow
        void answer.offsetHeight;
        // Now animate to the actual height with buffer to prevent cutoff
        answer.style.maxHeight = (height + 50) + 'px';
      } else {
        answer.style.maxHeight = '0px';
      }
    });
  }, [openIndex]);

  // Handle window resize to recalculate open answer heights
  useEffect(() => {
    const handleResize = () => {
      if (openIndex !== null) {
        const desktopAnswer = answerRefs.current[openIndex];
        const mobileAnswer = mobileAnswerRefs.current[openIndex];

        [desktopAnswer, mobileAnswer].forEach((answer) => {
          if (answer) {
            // Temporarily disable transition for instant resize adjustment
            const originalTransition = answer.style.transition;
            answer.style.transition = 'none';

            // Measure the new height
            answer.style.maxHeight = 'none';
            const height = answer.scrollHeight;
            answer.style.maxHeight = (height + 50) + 'px';

            // Force reflow to apply the instant change
            void answer.offsetHeight;

            // Re-enable transitions for future animations
            answer.style.transition = originalTransition;
          }
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [openIndex]);

  return (
    <div>
      {/* Custom scrollbar styles */}
      <style>{`
        /* Desktop scrollbar */
        @media (min-width: 769px) {
          .faq-scrollable::-webkit-scrollbar {
            width: 6px;
          }
          .faq-scrollable::-webkit-scrollbar-track {
            background: transparent;
          }
          .faq-scrollable::-webkit-scrollbar-thumb {
            background: rgba(251, 191, 36, 0.5);
            border-radius: 3px;
          }
          .faq-scrollable::-webkit-scrollbar-thumb:hover {
            background: rgba(251, 191, 36, 0.7);
          }
        }

        /* Hide scrollbar on mobile while keeping scroll functionality */
        @media (max-width: 768px) {
          .faq-scrollable::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          .faq-scrollable {
            -ms-overflow-style: none !important;
            scrollbar-width: none !important;
          }
        }
      `}</style>

      {/* Desktop Layout */}
      <motion.section
        ref={ref}
        className="relative w-full"
        style={{
          width: '100%',
          maxWidth: '1920px',
          margin: '0 auto',
          height: '700px',
          padding: 'clamp(40px, 6vw, 80px) clamp(20px, 5vw, 100px)',
          paddingBottom: 'clamp(40px, 6vw, 80px)',
          backgroundColor: '#000000',
          overflow: 'hidden',
          display: isMobileDevice() ? 'none' : 'flex',
          gap: 'clamp(30px, 5vw, 80px)',
          alignItems: 'flex-start',
          boxSizing: 'border-box'
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: isMobileDevice() ? 0 : 1 }}
        transition={{ duration: 0.3 }}
      >

        {/* Left Column - Heading & Description */}
        <motion.div
          style={{
            flex: '0 0 40%',
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(15px, 2vw, 30px)'
          }}
          initial={{ opacity: 0, x: -40 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
          transition={{ duration: 0.3, delay: 0, ease: 'easeOut' }}
        >
          {/* Section Heading - FREQUENT QUESTIONS */}
          <div>
            <h1 style={{
              lineHeight: '1.2',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: 'clamp(36px, 4vw, 64px)',
              color: 'rgba(255, 255, 255, 1)',
              margin: 0,
              padding: 0,
              fontVariationSettings: "'wght' 900"
            }}>
              FREQUENT
            </h1>
            <h1 style={{
              lineHeight: '1.2',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: 'clamp(36px, 4vw, 64px)',
              color: 'rgba(255, 255, 255, 1)',
              margin: 0,
              padding: 0,
              fontVariationSettings: "'wght' 900"
            }}>
              QUESTIONS
            </h1>
          </div>

          {/* FAQ Description */}
          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: 'clamp(11px, 1vw, 14px)',
            color: 'rgba(255, 255, 255, 1)',
            lineHeight: '1.5',
            margin: 0,
            padding: 0,
            fontVariationSettings: "'wght' 350"
          }}>
            Discover answers to the most frequently asked questions about Playcast, explore our innovative gaming platform, and see how we're transforming the way you play.
          </p>

          {/* Join the Discord Button */}
          <div>
            <a
              href={config.DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <motion.button
                className="aida-button aida-button-lg aida-button-outlined"
                style={{
                  width: 'auto',
                  minWidth: 'clamp(120px, 12vw, 180px)',
                  height: 'clamp(22px, 2vw, 32px)',
                  borderRadius: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: 'clamp(10px, 0.9vw, 13px)',
                  padding: '0 clamp(16px, 2vw, 24px)',
                  lineHeight: '1',
                  textAlign: 'center',
                  background: 'transparent',
                  color: '#FBBF24',
                  border: '1px solid #FBBF24'
                }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                Join the Discord
              </motion.button>
            </a>
          </div>
        </motion.div>

        {/* Right Column - FAQ Accordion */}
        <motion.div
          className="faq-scrollable"
          style={{
            flex: '1 1 60%',
            maxHeight: '600px',
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingRight: '10px',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(251, 191, 36, 0.5) transparent'
          }}
          initial={{ opacity: 0, x: 40 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 40 }}
          transition={{ duration: 0.3, delay: 0.1, ease: 'easeOut' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
            {faqData.map((faq, index) => (
              <div
                key={index}
                style={{
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  overflow: 'hidden'
                }}
              >
                {/* Question */}
                <button
                  onClick={() => toggleFAQ(index)}
                  style={{
                    width: '100%',
                    padding: 'clamp(14px, 1.5vw, 20px) 0',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'rgba(255, 255, 255, 1)',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 'clamp(12px, 1.1vw, 16px)',
                    fontWeight: 400,
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'color 0.2s',
                    gap: 'clamp(12px, 1.5vw, 20px)',
                    fontVariationSettings: "'wght' 500"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'rgba(251, 191, 36, 1)';
                    const icon = e.currentTarget.querySelector('.faq-icon') as HTMLElement;
                    if (icon) icon.style.color = 'rgba(251, 191, 36, 1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 1)';
                    const icon = e.currentTarget.querySelector('.faq-icon') as HTMLElement;
                    if (icon) icon.style.color = 'rgba(255, 255, 255, 1)';
                  }}
                >
                  <span style={{ flex: 1 }}>{faq.question}</span>
                  <span
                    className="faq-icon"
                    style={{
                      fontSize: 'clamp(16px, 1.5vw, 22px)',
                      fontWeight: 300,
                      color: 'rgba(255, 255, 255, 1)',
                      transform: openIndex === index ? 'rotate(45deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease, color 0.2s',
                      flexShrink: 0,
                      width: 'clamp(18px, 1.8vw, 26px)',
                      height: 'clamp(18px, 1.8vw, 26px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: '1'
                    }}>
                    +
                  </span>
                </button>

                {/* Answer */}
                <div
                  ref={(el) => {
                    answerRefs.current[index] = el;
                  }}
                  style={{
                    overflow: 'hidden',
                    transition: 'max-height 0.3s ease, padding-top 0.3s ease, padding-bottom 0.3s ease',
                    paddingTop: openIndex === index ? '10px' : '0px',
                    paddingBottom: openIndex === index ? 'clamp(16px, 1.8vw, 24px)' : '0px'
                  }}
                >
                  <div style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 'clamp(10px, 0.95vw, 11px)',
                    lineHeight: '1.5',
                    fontVariationSettings: "'wght' 350"
                  }}>
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.section>

      {/* Mobile Layout */}
      <motion.section
        ref={mobileRef}
        className="relative w-full"
        style={{
          width: '100vw',
          height: '1100px',
          backgroundColor: '#000000',
          overflow: 'hidden',
          padding: '40px 20px',
          display: isMobileDevice() ? 'flex' : 'none',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          paddingTop: '40px',
          boxSizing: 'border-box'
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: isMobileDevice() ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex flex-col items-center" style={{ width: '100%', maxWidth: '440px' }}>
          {/* Section Heading - FREQUENT QUESTIONS */}
          <motion.div
            style={{
              width: '100%',
              marginBottom: '20px',
              textAlign: 'center'
            }}
            initial={{ opacity: 0, y: -20 }}
            animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
            transition={{ duration: 0.3, delay: 0, ease: 'easeOut' }}
          >
            <h1 style={{
              lineHeight: '52px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '48px',
              color: 'rgba(255, 255, 255, 1)',
              margin: 0,
              padding: 0,
              fontVariationSettings: "'wght' 900"
            }}>
              FREQUENT
            </h1>
            <h1 style={{
              lineHeight: '48px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '48px',
              color: 'rgba(255, 255, 255, 1)',
              margin: 0,
              padding: 0,
              fontVariationSettings: "'wght' 900"
            }}>
              QUESTIONS
            </h1>
          </motion.div>

          {/* FAQ Accordion */}
          <motion.div
            style={{
              width: '100%'
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.3, delay: 0.1, ease: 'easeOut' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  style={{
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    overflow: 'hidden'
                  }}
                >
                  {/* Question */}
                  <button
                    onClick={() => toggleFAQ(index)}
                    style={{
                      width: '100%',
                      padding: '20px 0',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: 'rgba(255, 255, 255, 1)',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '19px',
                      fontWeight: 400,
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'color 0.2s',
                      gap: '16px',
                      fontVariationSettings: "'wght' 500"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'rgba(251, 191, 36, 1)';
                      const icon = e.currentTarget.querySelector('.faq-icon') as HTMLElement;
                      if (icon) icon.style.color = 'rgba(251, 191, 36, 1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 1)';
                      const icon = e.currentTarget.querySelector('.faq-icon') as HTMLElement;
                      if (icon) icon.style.color = 'rgba(255, 255, 255, 1)';
                    }}
                  >
                    <span style={{ flex: 1 }}>{faq.question}</span>
                    <span
                      className="faq-icon"
                      style={{
                        fontSize: '26px',
                        fontWeight: 300,
                        color: 'rgba(255, 255, 255, 1)',
                        transform: openIndex === index ? 'rotate(45deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease, color 0.2s',
                        flexShrink: 0,
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: '1'
                      }}>
                      +
                    </span>
                  </button>

                  {/* Answer */}
                  <div
                    ref={(el) => {
                      mobileAnswerRefs.current[index] = el;
                    }}
                    style={{
                      overflow: 'hidden',
                      transition: 'max-height 0.3s ease, padding-top 0.3s ease, padding-bottom 0.3s ease',
                      paddingTop: openIndex === index ? '10px' : '0px',
                      paddingBottom: openIndex === index ? '22px' : '0px'
                    }}
                  >
                    <div style={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '17px',
                      lineHeight: '24px',
                      fontVariationSettings: "'wght' 350"
                    }}>
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Join the Discord Button */}
          <div style={{ width: '100%', marginTop: '30px' }}>
            <a
              href={config.DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <motion.button
                className="aida-button aida-button-lg aida-button-outlined"
                style={{
                  width: '100%',
                  height: '40px',
                  borderRadius: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '18px',
                  padding: '0',
                  lineHeight: '1',
                  textAlign: 'center',
                  background: 'transparent',
                  color: '#FBBF24',
                  border: '1px solid #FBBF24'
                }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                Join the Discord
              </motion.button>
            </a>
          </div>
        </div>
      </motion.section>
    </div>
  );
};
